import { useCallback, useMemo, useState } from 'react';
import { DownloadIcon, FilePlus2Icon, FolderPlusIcon } from 'lucide-react';
import { Button, Spinner } from '@monksflow/monks-ui';
import { getWidgetStores, type WidgetFullModeProps } from '@/entities/widget';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { CenteredContent } from '@/shared/ui/layout/CenteredContent';
import { SidePanel, type WidgetPanelProps } from '@/shared/ui/SidePanelWrapper';
import { SaveToProjectPopover } from '@/shared/ui';
import { useSaveToProjectEnabled, useServerPdfDownload } from '@/shared/hooks';
import type { StrategicTerritoriesWidgetProps } from '../model/strategic-territories-widget.types';
import {
  downloadTerritoryPdf,
  saveStrategicTerritoriesArtifact,
} from '../api/strategic-territories-artifact.api';
import { TerritoryCard } from './components/TerritoryCard';
import { TerritoryDetail } from './components/TerritoryDetail';
import { useTranslation } from 'react-i18next';
import { useAgentCategoryChipColor } from '@/entities/agent';

export const StrategicTerritoriesWidget = (
  props: WidgetFullModeProps<StrategicTerritoriesWidgetProps>,
) => {
  const { t } = useTranslation('analysis');

  const data = props.data;
  const briefDeps = props.briefDeps;
  const readOnly = props.readOnly;

  // Store + chats access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store` + `@/features/chats` modules;
  // tests stub `getWidgetStores()` at the seam.
  const { widgetStore, chatStore } = getWidgetStores();
  const { useChatActions } = chatStore;

  const widget = widgetStore.useWidget();
  const { sendMessage } = useChatActions();

  if (!widget) {
    throw new Error('Widget not found');
  }

  const agentId = widget.agentName;
  const categoryChipColor = useAgentCategoryChipColor(agentId);
  const versionConfig = widgetStore.useWidgetVersions(agentId);

  const items = useMemo(() => data.items, [data.items]);

  // A saved single-territory artifact is rendered read-only in the Library
  // preview: open straight on the territory detail (not the one-card grid).
  // In the live chat the grid is the entry point, so default to null there.
  const isSingleTerritoryPreview = readOnly && items.length === 1;
  const [detailIndex, setDetailIndex] = useState<number | null>(() =>
    isSingleTerritoryPreview ? 0 : null,
  );
  const detailTerritory = detailIndex !== null ? items[detailIndex] : null;

  const saveEnabled = useSaveToProjectEnabled();
  const workspaceId = useCurrentWorkspaceId();
  const sourceArtifactId = typeof widget.artifactId === 'string' ? widget.artifactId : undefined;
  const showSaveAction = saveEnabled && !readOnly && !!detailTerritory;
  const showBriefAction = saveEnabled && !readOnly && !!detailTerritory;

  // The server PDF addresses the territory by artifact id and index.
  const canDownload = !!sourceArtifactId && !!workspaceId;
  const pdf = useServerPdfDownload({
    preparing: t('strategicTerritoriesWidget.preparingYourDownloadThisMay'),
    failed: t('strategicTerritoriesWidget.failedToDownloadStrategicTerritory'),
  });
  const isDownloading = detailIndex !== null && pdf.isDownloading(String(detailIndex));
  const { download: downloadPdf } = pdf;

  const handleDownload = useCallback(async () => {
    if (!sourceArtifactId || detailIndex === null || !workspaceId || !detailTerritory) return;
    await downloadPdf({
      key: String(detailIndex),
      fetchBlob: () =>
        downloadTerritoryPdf(sourceArtifactId, { workspaceId, itemIndex: detailIndex }),
      fileName: `${sanitizeDownloadFileName(detailTerritory.title, 'strategic-territory')}.pdf`,
      analytics: { document_type: 'strategic_territory', scope: 'item' },
    });
  }, [sourceArtifactId, detailIndex, workspaceId, detailTerritory, downloadPdf]);

  const sidePanelProps: WidgetPanelProps = {
    title: t('strategicTerritoriesWidget.strategy'),
    headerConfig: {
      chip: {
        label: t('strategicTerritoriesWidget.strategicTerritories'),
        color: categoryChipColor,
      },
      versionConfig,
      showDivider: true,
    },
    onClose: undefined,
  };

  const handleCloseDetail = () => {
    setDetailIndex(null);
  };

  const handleSelectTerritory = () => {
    if (!detailTerritory) throw new Error('No territory selected');

    void sendMessage({
      agentId,
      prompt: t('strategicTerritoriesWidget.continueWithTerritoryTitled', {
        title: detailTerritory.title,
      }),
      inputs: {
        territoryId: detailTerritory.id,
      },
      files: [],
    });
  };

  // Reset the selection during render if items shrunk below the current index,
  // instead of via useEffect.
  if (detailIndex !== null && detailIndex >= items.length) {
    setDetailIndex(null);
  }

  if (items.length === 0) {
    return (
      <SidePanel {...sidePanelProps}>
        <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
          <Spinner className="size-10" />
          <p className="text-heading-lg font-weight-medium text-foreground">
            {t('strategicTerritoriesWidget.generatingStrategicTerritories')}
          </p>
          <p className="text-body-lg text-muted-foreground">
            {t('strategicTerritoriesWidget.thisMayTakeAMoment')}
          </p>
        </div>
      </SidePanel>
    );
  }

  const detailSidePanelProps: WidgetPanelProps = {
    headerConfig: {
      backButton: {
        label: t('strategicTerritoriesWidget.back'),
        onClick: handleCloseDetail,
      },
      actions: [
        ...(canDownload
          ? [
              {
                type: 'icon' as const,
                icon: isDownloading ? (
                  <Spinner aria-hidden className="size-5" />
                ) : (
                  <DownloadIcon size={20} />
                ),
                onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  void handleDownload();
                },
                tooltip: t('strategicTerritoriesWidget.downloadStrategicTerritory'),
                ariaLabel: t('strategicTerritoriesWidget.downloadStrategicTerritory'),
                disabled: isDownloading || !detailTerritory,
              },
            ]
          : []),
        ...(showBriefAction && workspaceId
          ? [
              {
                type: 'custom' as const,
                tooltip: t('strategicTerritoriesWidget.startABrief'),
                component: (
                  <briefDeps.StartBrief>
                    <Button
                      aria-label={t('strategicTerritoriesWidget.startABrief')}
                      size="icon-sm"
                      variant="ghost"
                    >
                      <FilePlus2Icon size={20} />
                    </Button>
                  </briefDeps.StartBrief>
                ),
              },
            ]
          : []),
        ...(showSaveAction && sourceArtifactId && detailIndex !== null && workspaceId
          ? [
              {
                type: 'custom' as const,
                tooltip: t('strategicTerritoriesWidget.saveTerritoryToProject'),
                component: (
                  <SaveToProjectPopover
                    resourceId={`${sourceArtifactId}:${detailIndex}`}
                    workspaceId={workspaceId}
                    onMintArtifact={() =>
                      saveStrategicTerritoriesArtifact(sourceArtifactId, {
                        workspaceId,
                        itemIndex: detailIndex,
                      })
                    }
                  >
                    <Button
                      aria-label={t('strategicTerritoriesWidget.saveTerritoryToProject')}
                      size="icon-sm"
                      variant="ghost"
                      className="mr-2"
                    >
                      <FolderPlusIcon size={20} />
                    </Button>
                  </SaveToProjectPopover>
                ),
              },
            ]
          : []),
        {
          type: 'button' as const,
          label: t('strategicTerritoriesWidget.selectTerritory'),
          onClick: handleSelectTerritory,
          variant: 'contained' as const,
        },
      ],
      showDivider: true,
    },
  };

  if (isSingleTerritoryPreview && detailTerritory) {
    return (
      <div className="h-full overflow-y-auto">
        <CenteredContent className="px-10 py-6">
          <TerritoryDetail
            displayNavigation={false}
            territory={detailTerritory}
            onPrevious={() => {}}
            onNext={() => {}}
          />
        </CenteredContent>
      </div>
    );
  }

  const activeSidePanelProps = detailTerritory ? detailSidePanelProps : sidePanelProps;

  return (
    <SidePanel {...activeSidePanelProps}>
      <CenteredContent className="px-10 pb-10">
        {detailTerritory ? (
          <TerritoryDetail
            displayNavigation={items.length > 1}
            territory={detailTerritory}
            onPrevious={() => {
              setDetailIndex((prev) => (prev! - 1 + items.length) % items.length);
            }}
            onNext={() => {
              setDetailIndex((prev) => (prev! + 1) % items.length);
            }}
          />
        ) : (
          <>
            <p className="my-6 text-heading-xl">
              {t('strategicTerritoriesWidget.selectATerritoryToExplore')}
            </p>
            <div className="columns-1 gap-4 md:columns-2">
              {items.map((territory, index) => (
                <div key={territory.id} className="mb-4 break-inside-avoid">
                  <TerritoryCard territory={territory} onClick={() => setDetailIndex(index)} />
                </div>
              ))}
            </div>
          </>
        )}
      </CenteredContent>
    </SidePanel>
  );
};
